import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button, Spinner } from "react-bootstrap";
import { User } from "../../types/user";
import { fetchUserCases, fetchUserCasesImages, fetchUsers } from "../../services/httpClient";
import UserTab from "./UserTab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield, faUserTie } from "@fortawesome/free-solid-svg-icons";
import useApp from "../../utils/useApp";
import PageTitle from "../ui/PageTitle";
import SearchBar from "../ui/SearchBar";
import GuardianTab from "./GuardianTab";
import EditUser from "./EditUser";

interface UsersState {
  isActive?: boolean;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const Users = ({ isActive = true, setShowToast, setToastText, setToastBody }: UsersState) => {
  const { auth }: any = useApp();
  const searchRef = useRef<HTMLInputElement>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [orgAdminUsers, setOrgAdminUsers] = useState<User[]>([]);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [caseManagerUsers, setCaseManagerUsers] = useState<User[]>([]);
  const [resUsers, setResUsers] = useState<User[]>([]);
  const [supportUsers, setSupportUsers] = useState<User[]>([]);
  const [guardianUsers, setGuardianUsers] = useState<User[]>([]);
  const [view, setView] = useState<"staff" | "guardian">("staff");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<{ id?: string | number; createType?: string } | null>(null);

  useEffect(() => {
    getUsers();

    // trackNavigationEvent("switch_view", "admin_users");
  }, [auth?.profile?.userOrgID]);

  useEffect(() => {
    const updatedUsers = (workingUsers: User[]) =>
      workingUsers.map((user) => {
        const filteredCases = cases.filter(
          (c: any) =>
            c.primaryCM?.user_id === user?.id || c.secondaryCM?.user_id === user?.id || c.support?.some((s: any) => s.user_id === user?.id),
        );

        const activeCases = filteredCases.filter((c: any) => c.casestatus === "active");
        const inactiveCases = filteredCases.filter((c: any) => c.casestatus === "inactive");
        const waitlistCases = filteredCases.filter((c: any) => c.casestatus === "waitlist");

        return {
          ...user,
          cases: filteredCases,
          totalCases: filteredCases.length,
          activeCases: activeCases.length,
          inactiveCases: inactiveCases.length,
          waitlistCases: waitlistCases.length,
        };
      });

    var workingUsers: User[];
    workingUsers = users.filter((x: any) => x.role === "org_admin");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setOrgAdminUsers(workingUsers);
    }

    workingUsers = users.filter((x: any) => x.role === "admin");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setAdminUsers(workingUsers);
    }

    workingUsers = users.filter((x: any) => x.role === "case_manager");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setCaseManagerUsers(workingUsers);
    }

    //isHMIS
    setResUsers(users.filter((x: any) => x.role === "res_support"));

    workingUsers = users.filter((x: any) => x.role === "support");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setSupportUsers(workingUsers);
    }

    workingUsers = users.filter((x: any) => x.role === "family");
    const updatedFamilyUsers = workingUsers
      .map((user) => {
        let caseName = undefined;
        cases.forEach((c: any) => {
          if (c.casestatus === "active" && c.family.some((f: any) => f.user_id === user.id)) {
            caseName = c.name;
          }
        });

        return {
          ...user,
          casename: caseName,
        };
      })
      .filter((user) => user.casename !== undefined);
    setGuardianUsers(updatedFamilyUsers);
  }, [users, cases]);

  const getUsers = async () => {
    setIsLoading(true);
    await getCases();

    fetchUsers()
      .then((res: any) => {
        const userList = res[0].data;
        applyUsers(userList);
        setIsLoading(false);
        loadImages(userList);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  // /users returns profile_id only, so fetch the images separately and re-apply once they arrive
  const loadImages = (userList: any[]) => {
    const ids = userList.filter((u: any) => u.profile_id).map((u: any) => u.id);
    if (ids.length === 0) return;
    fetchUserCasesImages(ids, [])
      .then((res: any) => {
        const images: any[] = res?.[0]?.data?.data_fam || [];
        if (images.length === 0) return;
        const byUser = new Map(images.filter((i: any) => i.data).map((i: any) => [i.user_id, i]));
        applyUsers(
          userList.map((u: any) => (byUser.has(u.id) ? { ...u, profile: [{ id: byUser.get(u.id).id, data: byUser.get(u.id).data }] } : u)),
        );
      })
      .catch((err: any) => console.log(err));
  };

  const applyUsers = (userList: any[]) => {
    setUsers(userList);

    var workingUsers: User[];
    workingUsers = userList.filter((x: any) => x.role === "org_admin");
    setOrgAdminUsers(workingUsers);

    workingUsers = userList.filter((x: any) => x.role === "admin");
    setAdminUsers(workingUsers);

    workingUsers = userList.filter((x: any) => x.role === "case_manager");
    setCaseManagerUsers(workingUsers);

    //isHMIS
    setResUsers(userList.filter((x: any) => x.role === "res_support"));

    setSupportUsers(userList.filter((x: any) => x.role === "support"));

    workingUsers = userList.filter((x: any) => x.role === "family");
    setGuardianUsers(workingUsers);
  };

  const getCases = async () => {
    await fetchUserCases()
      .then((res: any) => {
        var c = res[0].data;
        setCases(c);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const closeEditor = () => {
    setEditing(null);
    setRoleFilter("all");
    setSearch("");
    getUsers();
  };

  // Settings keeps every tab mounted, so the search box can't rely on SearchBar's mount-time focus; focus it whenever this tab is shown
  useEffect(() => {
    if (isActive && !isLoading && !editing) {
      searchRef.current?.focus();
    }
  }, [isActive, isLoading, editing, view]);

  const staffGroups = [
    {
      type: "org_admin",
      displayType: "Org Admin",
      pill: "Org Admin",
      description: "Full access to every feature in the system, including all Organization settings.",
      users: orgAdminUsers,
    },
    {
      type: "admin",
      displayType: "Admin",
      pill: "Admin",
      description:
        "Manages day-to-day operations with access to My Cases, Cases, Groups, Announcements, Shelter, and Reports, plus the Details and Users sections of Organization Settings.",
      users: adminUsers,
    },
    {
      type: "case_manager",
      displayType: "Case Manager",
      pill: "Case Manager",
      description: "Works with their assigned cases through My Cases, Groups, and Announcements.",
      users: caseManagerUsers,
    },
    ...(auth?.profile?.includeHMIS === 1
      ? [
          {
            type: "res_support",
            displayType: "Residential Support",
            pill: "Residential Support",
            description: "Supports shelter operations with access to Shelter and Cases.",
            users: resUsers,
          },
        ]
      : []),
    {
      type: "support",
      displayType: "Support",
      pill: "Support",
      description: "Collaborates on a case through Messaging in My Cases.",
      users: supportUsers,
    },
  ];

  const query = search.trim().toLowerCase();
  const matchesSearch = (u: any) =>
    !query ||
    [u.firstname, u.lastname, u.preferredname, u.email, u.casename, `${u.firstname ?? ""} ${u.lastname ?? ""}`].some((v) =>
      (v ?? "").toString().toLowerCase().includes(query),
    );

  const visibleGroups = staffGroups
    .filter((g) => roleFilter === "all" || g.type === roleFilter)
    .map((g) => ({ ...g, filtered: g.users.filter(matchesSearch) }))
    // on All or while searching, hide groups with nothing to show; a single selected role still shows its "No ... Records" message
    .filter((g) => (roleFilter === "all" || query ? g.filtered.length > 0 : true));

  const selectedGroup = staffGroups.find((g) => g.type === roleFilter);
  const searchPlaceholder =
    view === "guardian" ? "Search Primary Adults" : selectedGroup ? `Search ${selectedGroup.displayType} Users` : "Search Users";

  const visibleGuardians = guardianUsers.filter(matchesSearch);

  const onViewChange = (newView: "staff" | "guardian") => {
    setView(newView);
    setRoleFilter("all");
    setSearch("");
  };

  return (
    <>
      {isLoading ? (
        <Button size="sm" className="spinner-button spinner-button-main">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </Button>
      ) : editing ? (
        <EditUser
          embeddedId={editing.id !== undefined ? String(editing.id) : undefined}
          embeddedType={editing.createType}
          onClose={closeEditor}
        />
      ) : (
        <Row>
          <Col xs={12} className="d-flex align-items-center mb-2">
            <div className="flex-grow-1">
              <PageTitle title="Users" type="section" />
            </div>
            <div className="d-flex align-items-center gap-3 flex-shrink-0">
              <Button
                data-testid="staff-view"
                className={view === "staff" ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => onViewChange("staff")}
              >
                <FontAwesomeIcon icon={faUserTie} className={"me-2"} />
                {"Staff"}
              </Button>
              <Button
                data-testid="guardian-view"
                className={view === "guardian" ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => onViewChange("guardian")}
              >
                <FontAwesomeIcon icon={faUserShield} className={"me-2"} />
                {"Primary Adults (" + guardianUsers.length + ")"}
              </Button>
              {view === "staff" && (
                <>
                  <div className="vr" />
                  <Button
                    data-testid="pagetitle-button-Create User"
                    className="app-primary-color button"
                    onClick={() => setEditing({ createType: roleFilter === "all" ? "org_admin" : roleFilter })}
                  >
                    Create User
                  </Button>
                </>
              )}
            </div>
          </Col>
          <Col xs={12} className="d-flex justify-content-center my-3">
            <SearchBar
              cssClass="search w-100"
              placeholder={searchPlaceholder}
              searchText={search}
              setSearchText={setSearch}
              searchLogic={() => {}}
              externalRef={searchRef}
            />
          </Col>
          {view === "staff" && (
            <Col xs={12}>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Button
                  data-testid="role-pill-all"
                  className={roleFilter === "all" ? "app-primary-color button" : "app-primary-color-inverse button"}
                  onClick={() => setRoleFilter("all")}
                >
                  {"All (" + staffGroups.reduce((n, g) => n + g.users.length, 0) + ")"}
                </Button>
                {staffGroups.map((g) => (
                  <Button
                    key={g.type}
                    data-testid={`role-pill-${g.type}`}
                    className={roleFilter === g.type ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => setRoleFilter(g.type)}
                  >
                    {g.pill + " (" + g.users.length + ")"}
                  </Button>
                ))}
              </div>
            </Col>
          )}
          <Col xs={12} className="mt-2">
            {view === "guardian" ? (
              <GuardianTab users={visibleGuardians} />
            ) : (
              <>
                {visibleGroups.length === 0 && <p className="mt-2">{query ? `No users match "${search}"` : "No Users"}</p>}
                {visibleGroups.map((g) => (
                  <div key={g.type} className="mb-4">
                    <UserTab
                      onEditUser={(id) => setEditing({ id })}
                      description={g.description}
                      users={g.filtered}
                      totalCount={g.users.length}
                      setUsers={setUsers}
                      setCases={setCases}
                      type={g.type}
                      displayType={g.displayType}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                    />
                  </div>
                ))}
              </>
            )}
          </Col>
        </Row>
      )}
    </>
  );
};

export default Users;
